import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { Truck, Clock, Globe, Package, AlertTriangle, Mail } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

const ShippingPage = () => {
  usePageSEO();
  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fdf2f4] rounded-full mb-4">
              <Truck className="w-8 h-8 text-[#b89a6d]" />
            </div>
            <h1 className="text-4xl font-bold text-[#3a2a26] mb-4">Shipping Policy</h1>
            <p className="text-[#7a6b62]">Last updated: February 27, 2026</p>
          </div>

          <div className="space-y-8">
            {/* Processing Time */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Clock className="w-6 h-6 text-[#b89a6d] mr-3" />
                Processing Time
              </h2>
              <p className="text-[#5a4a40] leading-relaxed mb-4">
                Orders are typically processed within 1-2 business days. Orders placed on weekends or holidays 
                will be processed on the next business day.
              </p>
              <p className="text-[#7a6b62] text-sm">
                You will receive a shipping confirmation email with tracking information once your order ships.
              </p>
            </section>

            {/* Shipping Options */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Package className="w-6 h-6 text-[#b89a6d] mr-3" />
                Shipping Options
              </h2>
              <div className="space-y-4">
                <div className="p-4 bg-[#fffaf5] border border-[#f0d8d4] rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[#3a2a26] font-medium">Standard Shipping</span>
                    <span className="text-[#b89a6d]">5-7 Business Days</span>
                  </div>
                  <p className="text-[#7a6b62] text-sm">Free on orders over $75. Otherwise $5.99.</p>
                </div>
                <div className="p-4 bg-[#fffaf5] border border-[#f0d8d4] rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[#3a2a26] font-medium">Expedited Shipping</span>
                    <span className="text-[#b89a6d]">2-3 Business Days</span>
                  </div>
                  <p className="text-[#7a6b62] text-sm">$12.99 flat rate.</p>
                </div>
                <div className="p-4 bg-[#fffaf5] border border-[#f0d8d4] rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[#3a2a26] font-medium">Overnight Shipping</span>
                    <span className="text-[#b89a6d]">1 Business Day</span>
                  </div>
                  <p className="text-[#7a6b62] text-sm">$24.99 flat rate. Orders must be placed before 12 PM EST.</p>
                </div>
              </div>
            </section>

            {/* Shipping Locations */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Globe className="w-6 h-6 text-[#b89a6d] mr-3" />
                Shipping Locations
              </h2>
              <div className="text-[#5a4a40] space-y-4">
                <p>
                  <strong className="text-[#3a2a26]">United States:</strong> We ship to all 50 states, including Alaska, Hawaii, 
                  and U.S. territories. Some remote areas may experience longer delivery times.
                </p>
                <p>
                  <strong className="text-[#3a2a26]">International:</strong> Currently, we only ship within the United States. 
                  We're working on expanding our shipping options and will update this policy when international shipping becomes available.
                </p>
              </div>
            </section>

            {/* Important Notes */}
            <section className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <AlertTriangle className="w-6 h-6 text-[#b89a6d] mr-3" />
                Important Information
              </h2>
              <ul className="text-[#5a4a40] space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">•</span>
                  <span>Delivery times are estimates and not guaranteed. Delays may occur due to carrier issues, weather, or other factors.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">•</span>
                  <span>Please ensure your shipping address is correct. We are not responsible for packages delivered to incorrect addresses provided by the customer.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">•</span>
                  <span>Some products may require temperature-controlled shipping. These items are shipped with appropriate packaging to maintain product integrity.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">•</span>
                  <span>P.O. Boxes may experience longer delivery times and are not eligible for expedited or overnight shipping.</span>
                </li>
              </ul>
            </section>

            {/* Lost/Damaged */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Lost or Damaged Packages</h2>
              <p className="text-[#5a4a40] leading-relaxed mb-4">
                If your package is lost or damaged during shipping, please contact us within 7 days of the expected 
                delivery date. We'll work with the carrier to resolve the issue and may offer a replacement or refund.
              </p>
              <p className="text-[#7a6b62] text-sm">
                Please save all packaging materials if your shipment arrives damaged, as they may be needed for carrier claims.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Mail className="w-6 h-6 text-[#b89a6d] mr-3" />
                Questions?
              </h2>
              <p className="text-[#5a4a40]">
                For shipping inquiries, contact us at:{" "}
                <a href="mailto:everything@8eight8.net" className="text-[#b89a6d] hover:underline">
                  everything@8eight8.net
                </a>
              </p>
            </section>

            {/* Related Links */}
            <div className="flex flex-wrap gap-4 justify-center pt-4">
              <Link 
                to="/refund" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Refund Policy
              </Link>
              <Link 
                to="/terms" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShippingPage;
